import type { WebContainer } from '@webcontainer/api';
import { persistUserFile } from '../runtime/tool-adapters/writeFile';
import { getMergedFileContent } from '../utils/mergedFiles';
import { isDesignSystemFilePath } from './editSource';
import type { HotEditListContext } from './listContext';

/**
 * JSX patching pulls in `@babel/*`, which expects `process.env.NODE_ENV` in the browser.
 * Dynamic import keeps Babel out of the initial bundle and avoids crashing app startup if `process`
 * is missing; Vite still should define `process.env.NODE_ENV` for when this chunk loads.
 */
export async function persistMergedClassName(opts: {
  wc: WebContainer;
  filePath: string;
  lineNumber: number;
  /** Tailwind utility deltas only — appended at call site, never replaces existing className */
  cnAppendOnly: string;
  columnNumber?: number;
  tagName?: string;
  componentName?: string;
  listContext?: HotEditListContext | null;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  if (isDesignSystemFilePath(opts.filePath)) {
    return { ok: false, error: 'Refusing to modify shared UI component file.' };
  }

  const append = opts.cnAppendOnly?.trim() ?? '';
  if (!append) return { ok: true };

  const source = getMergedFileContent(opts.filePath);
  if (!source) return { ok: false, error: `File not found in workspace: ${opts.filePath}` };

  const readFile = (p: string) => getMergedFileContent(p);

  const { patchJsxClassNameAtLine } = await import('./jsxPatcher');
  const r = patchJsxClassNameAtLine(source, opts.lineNumber, '', {
    cnAppendOnly: append,
    columnNumber: opts.columnNumber,
    tagName: opts.tagName,
    componentName: opts.componentName,
    listContext: opts.listContext,
    rootFilePath: opts.filePath,
    readFile,
  });
  if (!r.ok) return r;
  const outPath = r.writtenFilePath ?? opts.filePath;
  const outContent = r.content;
  if (outContent === getMergedFileContent(outPath)) return { ok: true };
  await persistUserFile(opts.wc, outPath, outContent, true);
  return { ok: true };
}

export async function persistTextEdit(opts: {
  wc: WebContainer;
  filePath: string;
  lineNumber: number;
  text: string;
  columnNumber?: number;
  tagName?: string;
  componentName?: string;
  hintText?: string;
  listContext?: HotEditListContext | null;
}): Promise<{ ok: true; writtenFilePath?: string } | { ok: false; error: string }> {
  if (isDesignSystemFilePath(opts.filePath)) {
    return {
      ok: false,
      error: 'Text edits must land on the page call site — not inside shared UI components.',
    };
  }

  const source = getMergedFileContent(opts.filePath);
  if (!source) return { ok: false, error: `File not found in workspace: ${opts.filePath}` };

  const readFile = (p: string) => getMergedFileContent(p);

  const { patchJsxTextAtLine } = await import('./jsxPatcher');
  const r = patchJsxTextAtLine(source, opts.lineNumber, opts.text, {
    columnNumber: opts.columnNumber,
    tagName: opts.tagName,
    componentName: opts.componentName,
    hintText: opts.hintText,
    listContext: opts.listContext,
    rootFilePath: opts.filePath,
    readFile,
  });
  if (!r.ok) return r;
  const outPath = r.writtenFilePath ?? opts.filePath;
  const outContent = r.content;
  if (outContent === getMergedFileContent(outPath)) return { ok: true, writtenFilePath: outPath };
  await persistUserFile(opts.wc, outPath, outContent, true);
  return { ok: true, writtenFilePath: outPath };
}

export async function persistSelectOptions(opts: {
  wc: WebContainer;
  filePath: string;
  lineNumber: number;
  options: string[];
  columnNumber?: number;
  tagName?: string;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  if (isDesignSystemFilePath(opts.filePath)) {
    return {
      ok: false,
      error: 'Select edits must land on the page call site — not inside shared UI components.',
    };
  }

  const source = getMergedFileContent(opts.filePath);
  if (!source) return { ok: false, error: `File not found in workspace: ${opts.filePath}` };

  const { patchJsxSelectOptionsAtLine } = await import('./jsxPatcher');
  const r = patchJsxSelectOptionsAtLine(source, opts.lineNumber, opts.options, {
    columnNumber: opts.columnNumber,
    tagName: opts.tagName ?? 'select',
  });
  if (!r.ok) return r;
  if (r.content === source) return { ok: true };
  await persistUserFile(opts.wc, opts.filePath, r.content, true);
  return { ok: true };
}

export async function persistJsxAttributeLiteral(opts: {
  wc: WebContainer;
  filePath: string;
  lineNumber: number;
  attributeName: string;
  literalValue: string;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const source = getMergedFileContent(opts.filePath);
  if (!source) return { ok: false, error: `File not found in workspace: ${opts.filePath}` };

  const { patchJsxAttributeLiteralAtLine } = await import('./jsxPatcher');
  const r = patchJsxAttributeLiteralAtLine(
    source,
    opts.lineNumber,
    opts.attributeName,
    opts.literalValue,
  );
  if (!r.ok) return r;
  if (r.content === source) return { ok: true };
  await persistUserFile(opts.wc, opts.filePath, r.content, true);
  return { ok: true };
}
